import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, TablePagination } from '@mui/material';
import { Visibility, Edit } from '@mui/icons-material';
import { employeeDetailService, userService } from '../services/api';
import EmployeeDetailDrilldown from './EmployeeDetailDrilldown';
import { usePagination } from '../hooks/usePagination';

const EmployeeDetails: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  // Unfiltered, unlike `users` below (which only feeds the Select-Employee dropdown and stays
  // active-only) — needed so the drilldown dialog can show full user info (email/role/isActive/
  // clientName) for a deactivated employee too, not just what's in the EmployeeDetail row.
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [allDetails, setAllDetails] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [formData, setFormData] = useState<any>({
    phoneNumber: '', address: '', dateOfBirth: '', joiningDate: '',
    department: '', designation: '', panNumber: '', aadharNumber: '',
    bankAccountNumber: '', bankName: '', bankIFSC: ''
  });
  const [files, setFiles] = useState<any>({ panCard: null, aadharCard: null, resume: null });
  const [message, setMessage] = useState('');
  const [viewUser, setViewUser] = useState<any>(null);
  const { pagedRows: pagedDetails, paginationProps: detailsPaginationProps } = usePagination(allDetails);

  useEffect(() => {
    loadUsers();
    loadAllDetails();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await userService.getUsers();
      setAllUsers(data || []);
      // Deactivated employees don't show up in the regular Select Employee list here —
      // only the Employees list page shows both active and inactive employees.
      setUsers((data || []).filter((u: any) => u.isActive));
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadAllDetails = async () => {
    try {
      const data = await employeeDetailService.getAllEmployeeDetails();
      setAllDetails(data);
    } catch (error) {
      console.error('Error loading details:', error);
    }
  };

  const loadEmployeeDetail = async (userId: number) => {
    try {
      const data = await employeeDetailService.getEmployeeDetail(userId);
      if (data.id) {
        setFormData({
          phoneNumber: data.phoneNumber || '',
          address: data.address || '',
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
          joiningDate: data.joiningDate ? data.joiningDate.split('T')[0] : '',
          department: data.department || '',
          designation: data.designation || '',
          panNumber: data.panNumber || '',
          aadharNumber: data.aadharNumber || '',
          bankAccountNumber: data.bankAccountNumber || '',
          bankName: data.bankName || '',
          bankIFSC: data.bankIFSC || ''
        });
      }
    } catch (error) {
      console.error('Error loading employee detail:', error);
    }
  };

  const handleUserSelect = (userId: number) => {
    setSelectedUserId(userId);
    loadEmployeeDetail(userId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) {
      setMessage('Please select an employee');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('userId', selectedUserId.toString());
    Object.keys(formData).forEach(key => {
      if (formData[key]) formDataToSend.append(key, formData[key]);
    });

    if (files.panCard) formDataToSend.append('panCardFile', files.panCard);
    if (files.aadharCard) formDataToSend.append('aadharCardFile', files.aadharCard);
    if (files.resume) formDataToSend.append('resumeFile', files.resume);

    try {
      const response = await employeeDetailService.saveEmployeeDetail(formDataToSend);
      setMessage(response.message);
      loadAllDetails();
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error saving details');
    }
  };

  const handleView = (userId: number) => {
    // Look up from the unfiltered list so this still works for a deactivated employee — the
    // active-only `users` list would come back empty for them.
    const user = allUsers.find(u => u.id === userId);
    if (user) setViewUser(user);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Employee Details Management</Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Add/Update Employee Details</Typography>
            {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField select fullWidth label="Select Employee" value={selectedUserId || ''} onChange={(e) => handleUserSelect(parseInt(e.target.value))} margin="normal" required>
                {users.map(user => (<MenuItem key={user.id} value={user.id}>{user.fullName}</MenuItem>))}
              </TextField>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField fullWidth label="Phone Number" value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} size="small" required />
                <TextField fullWidth label="Date of Birth" type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })} size="small" InputLabelProps={{ shrink: true }} required />
                <TextField fullWidth label="Joining Date" type="date" value={formData.joiningDate} onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })} size="small" InputLabelProps={{ shrink: true }} />
                <TextField fullWidth label="Department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} size="small" />
                <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth label="Designation" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} size="small" /></Box>
                <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth label="Address" multiline rows={2} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} size="small" required /></Box>
                <TextField fullWidth label="PAN Number" value={formData.panNumber} onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })} size="small" required />
                <TextField fullWidth label="Aadhar Number" value={formData.aadharNumber} onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })} size="small" required />
                <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth label="Bank Account Number" value={formData.bankAccountNumber} onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })} size="small" required /></Box>
                <TextField fullWidth label="Bank Name" value={formData.bankName} onChange={(e) => setFormData({ ...formData, bankName: e.target.value })} size="small" />
                <TextField fullWidth label="Bank IFSC" value={formData.bankIFSC} onChange={(e) => setFormData({ ...formData, bankIFSC: e.target.value })} size="small" />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>Upload Documents (* Required)</Typography>
                  <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>Upload PAN Card *<input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({ ...files, panCard: e.target.files?.[0] })} /></Button>
                  {files.panCard && <Typography variant="caption" color="success.main">{files.panCard.name}</Typography>}
                  <Button variant="outlined" component="label" fullWidth sx={{ mb: 1, mt: 1 }}>Upload Aadhar Card *<input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({ ...files, aadharCard: e.target.files?.[0] })} /></Button>
                  {files.aadharCard && <Typography variant="caption" color="success.main">{files.aadharCard.name}</Typography>}
                  <Button variant="outlined" component="label" fullWidth sx={{ mb: 1, mt: 1 }}>Upload Resume<input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setFiles({ ...files, resume: e.target.files?.[0] })} /></Button>
                  {files.resume && <Typography variant="caption">{files.resume.name}</Typography>}
                </Box>
              </Box>

              <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }}>Save Details</Button>
            </form>
          </Paper>
        </Box>

        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>All Employee Details</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>PAN Status</TableCell>
                    <TableCell>Aadhar Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDetails.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>{detail.userName}</TableCell>
                      <TableCell>{detail.department || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color={detail.panVerificationStatus === 'Approved' ? 'success.main' : detail.panVerificationStatus === 'Rejected' ? 'error.main' : 'warning.main'}>
                          {detail.panVerificationStatus || 'Pending'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color={detail.aadharVerificationStatus === 'Approved' ? 'success.main' : detail.aadharVerificationStatus === 'Rejected' ? 'error.main' : 'warning.main'}>
                          {detail.aadharVerificationStatus || 'Pending'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleView(detail.userId)}><Visibility /></IconButton>
                        <IconButton size="small" onClick={() => handleUserSelect(detail.userId)}><Edit /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {allDetails.length > 0 && <TablePagination {...detailsPaginationProps} />}
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      <EmployeeDetailDrilldown
        user={viewUser}
        onClose={() => setViewUser(null)}
        onDocumentVerified={loadAllDetails}
      />
    </Box>
  );
};

export default EmployeeDetails;
